import React, { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { useDynamicWidgets } from 'react-instantsearch';
import CloseIcon from '~/icons/close-thin.svg';
import ChevronLeftIcon from '~/icons/chevron-left.svg';
import { Accordion } from '~/shared/components';
import { ResetButton } from '~/shared/components/Button/ResetButton';
import { useTranslation } from '~/shared/utils/translation';
import { Drawer } from '~/shared/components/Drawer';
import { useFilterState } from '../../hooks/useFilterState';
import { useScrollLock } from '~/shared/hooks/useScrollLock';
import { useBannerState } from '~/features/navigation/hooks/useBannerState';
import Trigger from './Trigger/Trigger';
import { FilterListRow, SalesOffersFilterListRow } from './FilterListRow';
import { FilterAttributePanel, SalesOffersFilterAttributePanel } from './FilterAttributePanel';
import { useFilterAttribute } from './useFilterAttribute';
import { DrawerScrollContext } from '~/features/productList/components/VirtualizedList';
import { Facets } from '~/services/instantsearch';
import { colorSwatchKey } from '~/features/productList/hooks/useColorSwatches';
import { useVisibleAttributes } from '~/features/productList/hooks/useVisibleAttributes';
import { preDefinedAttributes } from './preDefinedAttributes';
import styles from './FilterDrawer.module.scss';

interface FilterDrawerProps {
    showDrawer?: boolean;
    productListRef?: React.RefObject<HTMLElement>;
    /** Real product listings (category or search) get the "PÅ LAGER" panel — gift cards don't. */
    showStoreAvailability?: boolean;
}

const FilterDrawer: FC<FilterDrawerProps> = ({
    showDrawer = true,
    productListRef,
    showStoreAvailability = false,
}) => {
    const { translate } = useTranslation();
    const {
        isOpen,
        initialAttribute,
        actions: { close, toggle, clearInitialAttribute, setIsDetailOpen },
    } = useFilterState();
    const { lock, unlock } = useScrollLock();
    const { headerHeight: bannerHeight } = useBannerState();
    const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
    const [activeAttribute, setActiveAttribute] = useState<string | null>(null);
    // Gates the list's fade-in-from-left animation so it doesn't also play on the drawer's own first open.
    const [hasNavigatedBack, setHasNavigatedBack] = useState(false);
    const headerRef = useRef<HTMLElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const [headerHeight, setHeaderHeight] = useState(0);
    const wasOpenRef = useRef(isOpen);
    const scrollYBeforeCloseRef = useRef(0);

    useEffect(() => {
        setIsDetailOpen(!!activeAttribute);
    }, [activeAttribute, setIsDetailOpen]);

    useEffect(() => () => setIsDetailOpen(false), [setIsDetailOpen]);

    // The header's height changes between the list view (single row) and the
    // detail view (back button row + title row), so the sticky search/selection
    // header inside `.content` needs its `top` offset measured, not hardcoded —
    // a mismatch there is what made the sticky content visibly jump on scroll.
    useEffect(() => {
        const node = headerRef.current;
        if (!node) return;

        const observer = new ResizeObserver(([entry]) => {
            // Round before comparing — ResizeObserver can report sub-pixel-
            // different heights across frames for the same visual size, and
            // setting state on every one of those creates a render loop.
            const height = Math.round(entry.contentRect.height);
            setHeaderHeight((prev) => (prev === height ? prev : height));
        });

        observer.observe(node);

        return () => observer.disconnect();
    }, []);

    const handleDrawerToggle = useCallback(() => {
        if (isOpen) {
            scrollYBeforeCloseRef.current = window.scrollY;
            scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
            setActiveAttribute(null);
        }

        toggle();
    }, [isOpen, scrollElement, toggle]);

    const handleClose = useCallback(() => {
        scrollYBeforeCloseRef.current = window.scrollY;
        scrollElement?.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        close();
        setActiveAttribute(null);
    }, [close, scrollElement]);

    // All facets' panels stay mounted (see FilterAttributePanel) and share this
    // one scroll container, so every VirtualizedList's scroll listener keeps
    // tracking its offset even while hidden. Without resetting scroll on every
    // switch, a facet can open already "scrolled" to wherever the last-viewed
    // facet or the list left off, rendering the wrong slice of its own items.
    const handleSelectAttribute = useCallback(
        (attribute: string) => {
            scrollElement?.scrollTo(0, 0);
            setActiveAttribute(attribute);
        },
        [scrollElement],
    );

    const handleBackToList = useCallback(() => {
        scrollElement?.scrollTo(0, 0);
        setActiveAttribute(null);
        setHasNavigatedBack(true);
    }, [scrollElement]);

    const { attributesToRender: attributesDynamic } = useDynamicWidgets({
        maxValuesPerFacet: 1000,
        facets: ['*'],
    });

    const candidateAttributes = useMemo(
        () => [
            ...(showStoreAvailability ? [preDefinedAttributes.stockAvailability] : []),
            preDefinedAttributes.newsToggles,
            ...attributesDynamic.filter((attribute) => attribute !== Facets.ACTIVE_PROMO_TYPE),
        ],
        [attributesDynamic, showStoreAvailability],
    );

    // Hides a facet with no matching options instead of showing an empty list.
    const attributesToRender = useVisibleAttributes(candidateAttributes, [
        preDefinedAttributes.stockAvailability,
        preDefinedAttributes.newsToggles,
        Facets.PRICE_RANGE,
        colorSwatchKey,
    ]);

    const activeFilter = useFilterAttribute(activeAttribute);

    // One-shot: opens straight into initialAttribute's detail view, then clears itself.
    useEffect(() => {
        if (isOpen && initialAttribute) {
            setActiveAttribute(initialAttribute);
            clearInitialAttribute();
        }
    }, [isOpen, initialAttribute, clearInitialAttribute]);

    // Handle scroll lock when drawer is open
    useEffect(() => {
        if (isOpen) {
            lock();
        } else {
            unlock();

            // Capture scroll position before close(). Drawer's own focus-return can silently move it.
            if (wasOpenRef.current && productListRef?.current && triggerRef.current) {
                const productListBottom =
                    productListRef.current.getBoundingClientRect().bottom + window.scrollY;
                const viewportBottomAtClose = scrollYBeforeCloseRef.current + window.innerHeight;

                if (viewportBottomAtClose > productListBottom) {
                    const triggerTop =
                        triggerRef.current.getBoundingClientRect().top + window.scrollY;
                    const gutterSize =
                        parseInt(
                            getComputedStyle(document.documentElement).getPropertyValue(
                                '--plp-gutter-size',
                            ),
                        ) || 16;
                    const targetPosition = triggerTop - bannerHeight - gutterSize;

                    window.scrollTo({ top: targetPosition, behavior: 'smooth' });
                }
            }
        }

        wasOpenRef.current = isOpen;

        return () => {
            unlock();
        };
    }, [isOpen, lock, unlock, productListRef, bannerHeight]);

    return (
        <>
            <Trigger
                ref={triggerRef}
                label={translate('plp.filterLabel')}
                handleToggle={handleDrawerToggle}
            />

            {showDrawer && (
                <Drawer
                    ref={setScrollElement}
                    open={isOpen}
                    onClose={handleClose}
                    highZIndex={true}
                    isFilterPanel
                    roundedCorners="top"
                    hasVirtualizedContent
                    className={styles.noSmoothScroll}
                >
                    <Accordion className={styles.wrapper}>
                        <header className={styles.header} ref={headerRef}>
                            <div className={styles.headerTopRow}>
                                {activeAttribute && (
                                    <ResetButton
                                        className={styles.back}
                                        aria-label={translate('plp.filterLabel')}
                                        onClick={handleBackToList}
                                    >
                                        <ChevronLeftIcon />
                                    </ResetButton>
                                )}

                                <ResetButton
                                    className={styles.close}
                                    aria-label="Close"
                                    onClick={handleClose}
                                >
                                    <CloseIcon className={styles.closeIcon} />
                                </ResetButton>
                            </div>

                            <span className={styles.headerTitle}>
                                {activeAttribute
                                    ? activeFilter.label
                                    : translate('plp.filterLabel')}
                            </span>
                        </header>

                        <div
                            className={styles.content}
                            style={
                                {
                                    '--sticky-header-offset': `${headerHeight}px`,
                                } as CSSProperties
                            }
                        >
                            <DrawerScrollContext.Provider value={scrollElement}>
                                <div
                                    hidden={!!activeAttribute}
                                    className={clsx(hasNavigatedBack && styles.enterBack)}
                                >
                                    {attributesToRender.map((attribute) =>
                                        attribute === preDefinedAttributes.newsToggles ? (
                                            <SalesOffersFilterListRow
                                                key={attribute}
                                                onSelect={handleSelectAttribute}
                                            />
                                        ) : (
                                            <FilterListRow
                                                key={attribute}
                                                attribute={attribute}
                                                onSelect={handleSelectAttribute}
                                            />
                                        ),
                                    )}
                                </div>

                                {attributesToRender.map((attribute) =>
                                    attribute === preDefinedAttributes.newsToggles ? (
                                        <SalesOffersFilterAttributePanel
                                            key={attribute}
                                            isActive={attribute === activeAttribute}
                                        />
                                    ) : (
                                        <FilterAttributePanel
                                            key={attribute}
                                            attribute={attribute}
                                            isActive={attribute === activeAttribute}
                                        />
                                    ),
                                )}
                            </DrawerScrollContext.Provider>
                        </div>
                    </Accordion>
                </Drawer>
            )}
        </>
    );
};

export default FilterDrawer;
